import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { usersAPI, agentsAPI } from '../services/api';
import { CompanyUser, Agent, Conversation, EmailThread } from '../types';
import usePermissions from '../hooks/usePermissions';
import { Bell, MessageSquare, Mail, Mic } from 'lucide-react';

import NotificationsTab from '../components/Inbox/NotificationsTab';
import ChatsTab from '../components/Inbox/ChatsTab';
import EmailsTab from '../components/Inbox/EmailsTab';
import VoiceTab from '../components/Inbox/VoiceTab';

type Tab = 'notifications' | 'chats' | 'emails' | 'voice';

const Inbox: React.FC = () => {
    const [searchParams, setSearchParams] = useSearchParams();

    const initialTab = (searchParams.get('tab') as Tab) || 'notifications';
    const [activeTab, setActiveTab] = useState<Tab>(initialTab);

    // Deep-link: auto-select a specific chat/email by ID
    const initialId = searchParams.get('id') || '';
    const [pendingSelectId, setPendingSelectId] = useState<string>(initialId);

    const initialComposeEmail = searchParams.get('action') === 'compose' ? searchParams.get('email') || '' : '';
    // A ticket's "email this customer" button also names the ticket's agent, so
    // the composer can preselect the mailbox instead of asking (Tickets.tsx).
    const initialComposeAgentId = searchParams.get('action') === 'compose' ? searchParams.get('agent') || '' : '';

    // In-app navigation (e.g. clicking a notification deep-link while
    // already on /inbox) updates `searchParams` via react-router but
    // doesn't remount this page, so the `initialTab` / `initialId`
    // constants above only run at mount. Re-sync state on every
    // searchParams change so a `<Link to="/inbox?tab=chats&id=...">`
    // click actually switches tabs and opens the target chat.
    //
    // Safe vs. the existing `onInitial*Handled` callbacks that delete
    // the `id` param after the child consumes it: that path leaves
    // `searchParams.get('id') === null`, this effect then sets
    // `pendingSelectId('')` — already the post-handoff value — so the
    // re-sync is a no-op rather than fighting the child's close flow.
    useEffect(() => {
        const newTab = (searchParams.get('tab') as Tab) || 'notifications';
        const newId = searchParams.get('id') || '';
        setActiveTab(newTab);
        setPendingSelectId(newId);
    }, [searchParams]);

    // Shared State
    const [agents, setAgents] = useState<Agent[]>([]);
    const [teamMembers, setTeamMembers] = useState<CompanyUser[]>([]);

    // Shared State for Selections to persist across tab switches
    const [selectedChat, setSelectedChat] = useState<Conversation | null>(null);
    const [selectedVoice, setSelectedVoice] = useState<Conversation | null>(null);
    const [selectedEmail, setSelectedEmail] = useState<EmailThread | null>(null);

    // Panel States
    const [showDetails, setShowDetails] = useState(false);
    const [mobilePanel, setMobilePanel] = useState<'list' | 'chat' | 'details'>('list');

    // Was inline here and prop-drilled into ChatsTab/VoiceTab/EmailsTab. The
    // widest of the legacy expressions: owner OR any of three roles.
    const { canAssignConversations: hasAssignPermission } = usePermissions();

    // Initial Load for Shared Data
    useEffect(() => {
        if (activeTab === 'chats' || activeTab === 'emails' || activeTab === 'voice') {
            if (agents.length === 0) fetchAgents();
            if (hasAssignPermission && teamMembers.length === 0) {
                fetchTeamMembers();
            }
        }
    }, [activeTab]);

    const fetchAgents = async () => {
        try {
            const res = await agentsAPI.list();
            setAgents(res.data);
        } catch (err) {
            console.error('Failed to load agents');
        }
    };

    const fetchTeamMembers = async () => {
        try {
            const res = await usersAPI.list();
            setTeamMembers(res.data);
        } catch (err) {
            console.error('Failed to load team members');
        }
    };

    const handleEmailNotificationClick = (_threadId: string) => {
        setActiveTab('emails');
        setMobilePanel('list');
        // The EmailsTab will handle auto-selecting the thread if we pass the ID down
        // For simplicity, we can fetch that specific thread or rely on the EmailsTab's
        // list loading then matching it. Our EmailsTab component's current structure
        // is designed to take a whole selectedEmail object.
        // We will reset selectedEmail to null so the list re-renders fresh
        // If we strictly want to open it, we would need to pass the threadId manually.
        setSelectedEmail(null);
    };

    return (
        <div className="flex h-[calc(100vh-80px)] -m-6 overflow-hidden">
            {/* ── Secondary Sidebar ── */}
            <div className="
                hidden md:flex
                w-52 lg:w-64 flex-shrink-0 flex-col
                border-r border-gray-200 dark:border-gray-800
                bg-white dark:bg-gray-900
                p-4
            ">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-6 px-2">Inbox</h2>
                <nav className="space-y-1">
                    <button
                        onClick={() => { setActiveTab('notifications'); setMobilePanel('list'); }}
                        className={`w-full flex items-center space-x-3 px-3 py-2.5 rounded-lg transition-colors ${activeTab === 'notifications'
                            ? 'bg-primary/10 text-primary font-medium'
                            : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800'
                            }`}
                    >
                        <Bell className="w-5 h-5" />
                        <span>Notifications</span>
                    </button>
                    <button
                        onClick={() => { setActiveTab('chats'); setMobilePanel('list'); }}
                        className={`w-full flex items-center space-x-3 px-3 py-2.5 rounded-lg transition-colors ${activeTab === 'chats'
                            ? 'bg-primary/10 text-primary font-medium'
                            : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800'
                            }`}
                    >
                        <MessageSquare className="w-5 h-5" />
                        <span>Chats</span>
                    </button>
                    <button
                        onClick={() => { setActiveTab('emails'); setMobilePanel('list'); }}
                        className={`w-full flex items-center space-x-3 px-3 py-2.5 rounded-lg transition-colors ${activeTab === 'emails'
                            ? 'bg-primary/10 text-primary font-medium'
                            : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800'
                            }`}
                    >
                        <Mail className="w-5 h-5" />
                        <span>Emails</span>
                    </button>
                    <button
                        onClick={() => { setActiveTab('voice'); setMobilePanel('list'); }}
                        className={`w-full flex items-center space-x-3 px-3 py-2.5 rounded-lg transition-colors ${activeTab === 'voice'
                            ? 'bg-primary/10 text-primary font-medium'
                            : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800'
                            }`}
                    >
                        <Mic className="w-5 h-5" />
                        <span>Voice</span>
                    </button>
                </nav>
            </div>

            {/* ── Main Content ── */}
            <div className="flex-1 flex flex-col overflow-hidden bg-gray-50 dark:bg-gray-950">
                {/* Mobile-only top tab bar */}
                <div className="flex md:hidden border-b border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 flex-shrink-0">
                    <button
                        onClick={() => { setActiveTab('notifications'); setMobilePanel('list'); }}
                        className={`flex-1 flex items-center justify-center gap-2 py-3 text-sm font-medium border-b-2 transition-colors ${activeTab === 'notifications'
                            ? 'border-primary text-primary'
                            : 'border-transparent text-gray-500 dark:text-gray-400'
                            }`}
                    >
                        <Bell className="w-4 h-4" />
                        Notifications
                    </button>
                    <button
                        onClick={() => { setActiveTab('chats'); setMobilePanel('list'); setSelectedChat(null); setShowDetails(false); }}
                        className={`flex-1 flex items-center justify-center gap-2 py-3 text-sm font-medium border-b-2 transition-colors ${activeTab === 'chats'
                            ? 'border-primary text-primary'
                            : 'border-transparent text-gray-500 dark:text-gray-400'
                            }`}
                    >
                        <MessageSquare className="w-4 h-4" />
                        Chats
                    </button>
                    <button
                        onClick={() => { setActiveTab('emails'); setMobilePanel('list'); setSelectedEmail(null); setShowDetails(false); }}
                        className={`flex-1 flex items-center justify-center gap-2 py-3 text-sm font-medium border-b-2 transition-colors ${activeTab === 'emails'
                            ? 'border-primary text-primary'
                            : 'border-transparent text-gray-500 dark:text-gray-400'
                            }`}
                    >
                        <Mail className="w-4 h-4" />
                        Emails
                    </button>
                    <button
                        onClick={() => { setActiveTab('voice'); setMobilePanel('list'); setSelectedVoice(null); setShowDetails(false); }}
                        className={`flex-1 flex items-center justify-center gap-2 py-3 text-sm font-medium border-b-2 transition-colors ${activeTab === 'voice'
                            ? 'border-primary text-primary'
                            : 'border-transparent text-gray-500 dark:text-gray-400'
                            }`}
                    >
                        <Mic className="w-4 h-4" />
                        Voice
                    </button>
                </div>

                {/* Content Area */}
                {activeTab === 'notifications' && (
                    <NotificationsTab
                        onEmailNotificationClick={handleEmailNotificationClick}
                    />
                )}
                {activeTab === 'chats' && (
                    <ChatsTab
                        agents={agents}
                        teamMembers={teamMembers}
                        hasAssignPermission={hasAssignPermission}
                        mobilePanel={mobilePanel}
                        selectedChat={selectedChat}
                        setSelectedChat={setSelectedChat}
                        setMobilePanel={setMobilePanel}
                        showDetails={showDetails}
                        setShowDetails={setShowDetails}
                        initialChatId={pendingSelectId}
                        onInitialChatHandled={() => {
                            setPendingSelectId('');
                            if (searchParams.has('id')) {
                                searchParams.delete('id');
                                setSearchParams(searchParams, { replace: true });
                            }
                        }}
                    />
                )}
                {activeTab === 'voice' && (
                    <VoiceTab
                        agents={agents}
                        teamMembers={teamMembers}
                        hasAssignPermission={hasAssignPermission}
                        mobilePanel={mobilePanel}
                        selectedChat={selectedVoice}
                        setSelectedChat={setSelectedVoice}
                        setMobilePanel={setMobilePanel}
                        showDetails={showDetails}
                        setShowDetails={setShowDetails}
                    />
                )}
                {activeTab === 'emails' && (
                    <EmailsTab
                        agents={agents}
                        teamMembers={teamMembers}
                        hasAssignPermission={hasAssignPermission}
                        mobilePanel={mobilePanel}
                        selectedEmail={selectedEmail}
                        setSelectedEmail={setSelectedEmail}
                        setMobilePanel={setMobilePanel}
                        showDetails={showDetails}
                        setShowDetails={setShowDetails}
                        initialComposeEmail={initialComposeEmail}
                        initialComposeAgentId={initialComposeAgentId}
                        initialEmailId={pendingSelectId}
                        onInitialEmailHandled={() => {
                            setPendingSelectId('');
                            if (searchParams.has('id')) {
                                searchParams.delete('id');
                                setSearchParams(searchParams, { replace: true });
                            }
                        }}
                        onComposeHandled={() => {
                            if (searchParams.has('action')) {
                                searchParams.delete('action');
                                searchParams.delete('email');
                                searchParams.delete('agent');
                                setSearchParams(searchParams, { replace: true });
                            }
                        }}
                    />
                )}
            </div>
        </div>
    );
};

export default Inbox;